import { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { adminGetEvents, adminGetEventsSummary, adminGetEventsByTenant, adminGetEventsAlerts } from '../../lib/adminApi';

const BLUE   = '#0176CE';
const RED    = '#dc2626';
const ORANGE = '#f59e0b';
const GREEN  = '#10b981';

const EVENT_ICONS = {
  page_view:             '👁',
  upload_validated:      '✅',
  upload_success:        '📤',
  upload_failed:         '❌',
  job_trial_started:     '🧪',
  payment_initiated:     '💳',
  payment_failed:        '🚨',
  job_started:           '⚙',
  results_viewed:        '📊',
  masterfile_viewed:     '📂',
  masterfile_downloaded: '⬇',
  api_error:             '🔴',
  js_error:              '🔴',
  login_success:         '🔑',
  login_failed:          '🔒',
  ip_blocked:            '🚫',
};

const ALERT_CONFIG = {
  upload_failed:   { label: 'Upload Failures',   color: RED,    bg: 'rgba(220,38,38,0.08)',   border: '#fecaca' },
  payment_failed:  { label: 'Payment Failures',  color: RED,    bg: 'rgba(220,38,38,0.08)',   border: '#fecaca' },
  api_error:       { label: 'API Errors',         color: ORANGE, bg: 'rgba(245,158,11,0.08)', border: '#fde68a' },
  js_error:        { label: 'JS Errors',          color: ORANGE, bg: 'rgba(245,158,11,0.08)', border: '#fde68a' },
  login_failed:    { label: 'Failed Logins',      color: ORANGE, bg: 'rgba(245,158,11,0.08)', border: '#fde68a' },
  ip_blocked:      { label: 'IPs Blocked',        color: RED,    bg: 'rgba(220,38,38,0.08)',  border: '#fecaca' },
};

const ERROR_TYPES = new Set(['upload_failed','payment_failed','api_error','js_error','login_failed','ip_blocked']);
const DAYS_OPTIONS = [1, 7, 14, 30];

function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtRelative(d) {
  if (!d) return '—';
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs/24)}d ago`;
}

function SummaryCard({ label, value, color, sub }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 20px', flex: 1, minWidth: 100 }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || '#0f172a' }}>{value ?? '—'}</div>
      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 3 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function EventBadge({ type }) {
  const isError = ERROR_TYPES.has(type);
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
      background: isError ? 'rgba(239,68,68,0.1)' : 'rgba(1,118,206,0.08)',
      color: isError ? RED : BLUE, whiteSpace: 'nowrap' }}>
      {EVENT_ICONS[type] || '•'} {type.replace(/_/g, ' ')}
    </span>
  );
}

// ── Alerts Tab ────────────────────────────────────────────────────────────────
function AlertsTab({ days }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    setLoading(true);
    adminGetEventsAlerts(days)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <div style={{ color: '#9ca3af', padding: '40px', textAlign: 'center' }}>Loading…</div>;
  if (!data)   return <div style={{ color: '#9ca3af', padding: '40px', textAlign: 'center' }}>Failed to load alerts.</div>;

  const countMap = {};
  const lastMap  = {};
  (data.counts || []).forEach(c => { countMap[c.event_type] = c.cnt; lastMap[c.event_type] = c.last_occurred; });

  const totalAlerts = Object.values(countMap).reduce((a,b) => a + b, 0);

  return (
    <div>
      {/* Top summary row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <SummaryCard label="Total Alerts"     value={totalAlerts} color={totalAlerts > 0 ? RED : GREEN} />
        <SummaryCard label="Upload Failures"  value={countMap.upload_failed  || 0} color={(countMap.upload_failed  || 0) > 0 ? RED    : GREEN} />
        <SummaryCard label="Payment Failures" value={countMap.payment_failed || 0} color={(countMap.payment_failed || 0) > 0 ? RED    : GREEN} />
        <SummaryCard label="API Errors"       value={countMap.api_error      || 0} color={(countMap.api_error      || 0) > 0 ? ORANGE : GREEN} />
        <SummaryCard label="Failed Logins"    value={countMap.login_failed   || 0} color={(countMap.login_failed   || 0) > 0 ? ORANGE : GREEN} />
        <SummaryCard label="IPs Blocked"      value={data.active_blocks       || 0} color={(data.active_blocks      || 0) > 0 ? RED    : GREEN} sub="currently active" />
      </div>

      {/* Alert cards by type */}
      {Object.entries(ALERT_CONFIG).map(([type, cfg]) => {
        const count = countMap[type] || 0;
        if (count === 0) return null;
        const items = (data.recent || []).filter(e => e.event_type === type).slice(0, 10);
        const isOpen = expanded[type];

        return (
          <div key={type} style={{ border: `1px solid ${cfg.border}`, borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
            {/* Card header */}
            <div onClick={() => setExpanded(prev => ({ ...prev, [type]: !prev[type] }))}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
                background: cfg.bg, cursor: 'pointer', userSelect: 'none' }}>
              <span style={{ fontSize: 18 }}>{EVENT_ICONS[type] || '⚠'}</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: cfg.color }}>{cfg.label}</span>
                <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 12 }}>last: {fmtRelative(lastMap[type])}</span>
              </div>
              <span style={{ fontSize: 22, fontWeight: 800, color: cfg.color, minWidth: 40, textAlign: 'right' }}>{count}</span>
              <span style={{ color: cfg.color, fontSize: 11, opacity: 0.7, transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block', transition: 'transform 0.2s' }}>▶</span>
            </div>

            {/* Expanded events list */}
            {isOpen && (
              <div>
                {items.map(e => {
                  const meta = e.metadata ? (typeof e.metadata === 'string' ? (() => { try { return JSON.parse(e.metadata); } catch { return {}; } })() : e.metadata) : {};
                  const detail = e.error_message || meta.error || meta.filename || '';
                  return (
                    <div key={e.event_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px', borderTop: '1px solid #f1f5f9' }}>
                      <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap', width: 120, flexShrink: 0 }}>{fmtTime(e.created_at)}</span>
                      <span style={{ fontSize: 12, fontWeight: 500, width: 140, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.tenant_name || '—'}</span>
                      <span style={{ fontSize: 12, color: '#6b7280', width: 160, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.user_email || '—'}</span>
                      <span style={{ fontSize: 12, color: RED, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(detail || '—').slice(0, 120)}</span>
                    </div>
                  );
                })}
                {count > 10 && (
                  <div style={{ padding: '10px 18px', fontSize: 12, color: '#9ca3af', borderTop: '1px solid #f1f5f9' }}>
                    + {count - 10} more — use Live Feed with event type filter to see all
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {totalAlerts === 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '48px', textAlign: 'center', color: '#10b981', fontSize: 16, fontWeight: 600 }}>
          ✓ No alerts in the last {days} day{days !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

// ── By Tenant Tab ─────────────────────────────────────────────────────────────
function ByTenantTab({ days, onDrillDown }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort]       = useState('total_events');

  useEffect(() => {
    setLoading(true);
    adminGetEventsByTenant(days)
      .then(r => setTenants(r.tenants || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <div style={{ color: '#9ca3af', padding: '40px', textAlign: 'center' }}>Loading…</div>;
  if (!tenants.length) return <div style={{ color: '#9ca3af', padding: '40px', textAlign: 'center' }}>No tenant activity in this window.</div>;

  const sorted = [...tenants].sort((a,b) => (b[sort] || 0) - (a[sort] || 0));

  const cols = [
    { key: 'total_events', label: 'Total' },
    { key: 'logins',       label: 'Logins' },
    { key: 'uploads',      label: 'Uploads' },
    { key: 'jobs_started', label: 'Jobs' },
    { key: 'payments',     label: 'Payments' },
    { key: 'results_views',label: 'Results' },
    { key: 'errors',       label: 'Errors' },
    { key: 'unique_users', label: 'Users' },
    { key: 'sessions',     label: 'Sessions' },
  ];

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={thStyle}>Tenant</th>
            {cols.map(c => (
              <th key={c.key} onClick={() => setSort(c.key)}
                style={{ ...thStyle, cursor: 'pointer', color: sort === c.key ? BLUE : '#6b7280',
                  background: sort === c.key ? '#f0f7ff' : '#f8fafc' }}>
                {c.label} {sort === c.key ? '↓' : ''}
              </th>
            ))}
            <th style={thStyle}>Last Seen</th>
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(t => (
            <tr key={t.tenant_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
              <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600 }}>{t.company_name || `Tenant #${t.tenant_id}`}</td>
              {cols.map(c => (
                <td key={c.key} style={{ padding: '10px 12px', fontSize: 12, textAlign: 'center',
                  color: c.key === 'errors' && t[c.key] > 0 ? RED : (c.key === 'total_events' ? '#0f172a' : '#374151'),
                  fontWeight: c.key === 'total_events' ? 700 : (c.key === 'errors' && t[c.key] > 0 ? 700 : 400) }}>
                  {t[c.key] || 0}
                </td>
              ))}
              <td style={{ padding: '10px 12px', fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>{fmtRelative(t.last_seen)}</td>
              <td style={{ padding: '10px 12px' }}>
                <button onClick={() => onDrillDown(String(t.tenant_id))}
                  style={{ background: 'none', border: `1px solid ${BLUE}`, borderRadius: 6, color: BLUE, fontSize: 11, padding: '3px 10px', cursor: 'pointer' }}>
                  Feed →
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const thStyle = { padding: '10px 12px', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' };

// ── Main Page ─────────────────────────────────────────────────────────────────
const TABS = ['alerts', 'by-tenant', 'feed', 'errors'];

export default function AdminActivity() {
  const [tab,          setTab]          = useState('alerts');
  const [days,         setDays]         = useState(7);
  const [summary,      setSummary]      = useState(null);
  const [events,       setEvents]       = useState([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [loading,      setLoading]      = useState(false);
  const [filterTenant, setFilterTenant] = useState('');
  const [filterType,   setFilterType]   = useState('');

  const loadSummary = useCallback(async () => {
    try { setSummary(await adminGetEventsSummary(days)); } catch (_) {}
  }, [days]);

  const loadEvents = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await adminGetEvents({
        days, page: p,
        tenant_id: filterTenant || undefined,
        type:      filterType   || undefined,
        category:  tab === 'errors' ? 'errors' : undefined,
      });
      if (p === 1) setEvents(res.events || []);
      else setEvents(prev => [...prev, ...(res.events || [])]);
      setTotal(res.total || 0);
      setPage(p);
    } catch (_) {}
    finally { setLoading(false); }
  }, [days, tab, filterTenant, filterType]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => {
    if (tab === 'feed' || tab === 'errors') loadEvents(1);
  }, [loadEvents, tab]);

  function drillDownTenant(tenantId) {
    setFilterTenant(tenantId);
    setTab('feed');
  }

  const hasMore = events.length < total;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <AdminSidebar active="activity" />
      <main style={{ flex: 1, padding: '36px 40px', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0a2540', margin: 0 }}>User Activity</h1>
            <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Alerts, per-tenant reports, and live event feed</p>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {DAYS_OPTIONS.map(d => (
              <button key={d} onClick={() => setDays(d)}
                style={{ background: days === d ? BLUE : '#fff', color: days === d ? '#fff' : '#374151', border: '1px solid #e2e8f0', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {d}d
              </button>
            ))}
          </div>
        </div>

        {/* Summary cards */}
        {summary && (
          <div style={{ display: 'flex', gap: 14, marginBottom: 28, flexWrap: 'wrap' }}>
            <SummaryCard label="Active Tenants"   value={summary.summary?.active_tenants} />
            <SummaryCard label="Active Users"     value={summary.summary?.active_users} />
            <SummaryCard label="Uploads"          value={summary.summary?.tenants_uploaded} />
            <SummaryCard label="Jobs Processed"   value={summary.summary?.tenants_processed} />
            <SummaryCard label="Error Events"     value={summary.summary?.error_events}    color={summary.summary?.error_events    > 0 ? RED    : GREEN} />
            <SummaryCard label="Upload Failures"  value={summary.summary?.upload_failures}  color={summary.summary?.upload_failures  > 0 ? ORANGE : GREEN} />
            <SummaryCard label="Payment Failures" value={summary.summary?.payment_failures} color={summary.summary?.payment_failures > 0 ? ORANGE : GREEN} />
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #e2e8f0', marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 2 }}>
            {[
              { key: 'alerts',    label: '🚨 Alerts' },
              { key: 'by-tenant', label: '🏢 By Tenant' },
              { key: 'feed',      label: 'Live Feed' },
              { key: 'errors',    label: 'Errors' },
            ].map(t => (
              <button key={t.key} onClick={() => { setTab(t.key); setPage(1); }}
                style={{ background: 'none', border: 'none', padding: '8px 18px', fontSize: 13,
                  fontWeight: tab === t.key ? 700 : 500, color: tab === t.key ? BLUE : '#6b7280',
                  cursor: 'pointer', borderBottom: tab === t.key ? `2px solid ${BLUE}` : '2px solid transparent', marginBottom: -2 }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Feed/errors filters */}
          {(tab === 'feed' || tab === 'errors') && (
            <div style={{ display: 'flex', gap: 8 }}>
              <input placeholder="Filter tenant ID…" value={filterTenant} onChange={e => setFilterTenant(e.target.value)}
                style={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', width: 130 }} />
              <input placeholder="Filter event type…" value={filterType} onChange={e => setFilterType(e.target.value)}
                style={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', width: 150 }} />
              <button onClick={() => { setFilterTenant(''); setFilterType(''); }}
                style={{ fontSize: 12, color: '#6b7280', background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Tab content */}
        {tab === 'alerts' && <AlertsTab days={days} />}
        {tab === 'by-tenant' && <ByTenantTab days={days} onDrillDown={drillDownTenant} />}

        {(tab === 'feed' || tab === 'errors') && (
          <>
            {loading && <div style={{ color: '#9ca3af', padding: '40px', textAlign: 'center' }}>Loading…</div>}
            {!loading && events.length === 0 && <div style={{ color: '#9ca3af', padding: '40px', textAlign: 'center' }}>No events found.</div>}
            {!loading && events.length > 0 && (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Time', 'Tenant', 'User', 'Event', 'Page', 'Detail'].map(h => (
                        <th key={h} style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {events.map(e => {
                      const meta = e.metadata ? (typeof e.metadata === 'string' ? (() => { try { return JSON.parse(e.metadata); } catch { return {}; } })() : e.metadata) : {};
                      const detail = e.error_message || meta.error || meta.filename || meta.job_id || meta.amount_usd || '';
                      return (
                        <tr key={e.event_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px 16px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtTime(e.created_at)}</td>
                          <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 500 }}>
                            <span style={{ cursor: 'pointer', color: BLUE }} onClick={() => drillDownTenant(String(e.tenant_id))}>
                              {e.tenant_name || (e.tenant_id ? `#${e.tenant_id}` : '—')}
                            </span>
                          </td>
                          <td style={{ padding: '10px 16px', fontSize: 12, color: '#6b7280' }}>{e.user_email || '—'}</td>
                          <td style={{ padding: '10px 16px' }}><EventBadge type={e.event_type} /></td>
                          <td style={{ padding: '10px 16px', fontSize: 12, color: '#6b7280' }}>{e.page || '—'}</td>
                          <td style={{ padding: '10px 16px', fontSize: 12, color: ERROR_TYPES.has(e.event_type) ? RED : '#374151', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {String(detail || '—').slice(0, 100)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {hasMore && (
                  <div style={{ padding: '16px', textAlign: 'center', borderTop: '1px solid #e2e8f0' }}>
                    <button onClick={() => loadEvents(page + 1)}
                      style={{ background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 7, color: '#374151', cursor: 'pointer', fontSize: 13, padding: '9px 24px' }}>
                      Load more ({(total - events.length).toLocaleString()} remaining)
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

      </main>
    </div>
  );
}
